import type { MonthlyCombatPower } from '../../../types/combat-power.js';

/** @graph-connects none */
export function calcAwards(
  members: MonthlyCombatPower[],
  currentMonthIdx: number,
  labelFn?: (name: string) => string,
): {
  topContributors: { name: string; pct: number }[];
  risingStars: { name: string; pct: number }[];
} {
  const label = labelFn ?? ((n: string) => n);

  const currentActuals = members
    .map((m) => {
      const cm = m.months[currentMonthIdx];
      const pt =
        cm && cm.actualPower !== null && cm.businessDays > 0 ? cm.businessDays * cm.actualPower : 0;
      return { name: label(m.name), pt };
    })
    .filter((x) => x.pt > 0);
  const totalPt = currentActuals.reduce((s, x) => s + x.pt, 0);
  const topContributors = currentActuals
    .map((x) => ({ name: x.name, pct: totalPt > 0 ? (x.pt / totalPt) * 100 : 0 }))
    .sort((a, b) => b.pct - a.pct)
    .slice(0, 3);

  const prevIdx = currentMonthIdx - 1;
  const risingStars = members
    .map((m) => {
      const cur = m.months[currentMonthIdx]?.actualPower;
      const prev = prevIdx >= 0 ? m.months[prevIdx]?.actualPower : null;
      if (cur === null || cur === undefined || prev === null || prev === undefined || prev <= 0) {
        return null;
      }
      return { name: label(m.name), pct: ((cur - prev) / prev) * 100 };
    })
    .filter((x): x is { name: string; pct: number } => x !== null && x.pct > -100)
    .sort((a, b) => b.pct - a.pct)
    .slice(0, 3);

  return { topContributors, risingStars };
}

/** @graph-connects none */
export function AwardBlock({
  title,
  items,
  crownColor,
}: {
  title: string;
  items: { name: string; pct: number }[];
  crownColor: string;
}) {
  if (items.length === 0) return null;
  return (
    <div className="bg-[var(--color-bg-elevated)] rounded-lg p-3 min-w-[160px]">
      <h4 className="text-xs font-bold text-[var(--color-text-muted)] mb-2">{title}</h4>
      <div className="space-y-1.5">
        {items.map((item, i) => (
          <div
            key={item.name}
            className={`flex items-center gap-1.5 ${i === 0 ? 'text-sm font-bold' : 'text-xs'} text-[var(--color-text-primary)]`}
          >
            {i === 0 && <span style={{ color: crownColor, fontSize: '14px' }}>👑</span>}
            {i > 0 && (
              <span className="text-[var(--color-text-muted)] w-4 text-center">
                {String(i + 1)}
              </span>
            )}
            <span className="truncate">{item.name}</span>
            <span
              className={`ml-auto whitespace-nowrap ${i === 0 ? '' : 'text-[var(--color-text-secondary)]'}`}
            >
              {item.pct >= 0 ? '+' : ''}
              {item.pct.toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
